// src/components/StudentForm.tsx
import React, {
    useState,
    useRef,
    FormEvent,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import {
    CreateStudentRequest,
    EnrollmentInput,
    InitialPayment,
} from '../types/student';
import { getCourses } from '../api/courses';
import { AuthContext } from '../context/AuthContext';
import Cropper, { Area } from 'react-easy-crop';

const BackArrowIcon = () => (
    <svg width="20" height="20" className="block" viewBox="0 0 24 24" fill="none">
        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ——— Inlined crop helpers —————————————————————————————————————————
const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new window.Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.src = url;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
    });

async function getCroppedImg(imageSrc: string, crop: Area): Promise<string | null> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
    );
    return canvas.toDataURL('image/jpeg');
}

function dataURLtoFile(dataurl: string, filename: string) {
    const arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)![1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], filename, { type: mime });
}
// ————————————————————————————————————————————————————————————————

interface Props {
    onSubmit: (data: CreateStudentRequest | FormData) => Promise<void>;
    onCancel: () => void;
}

interface CourseOption {
    id: string;
    name: string;
    baseFee: number;
    maxViewerDiscount: number;
}

interface EnrollmentRow {
    course: string;
    discount: string;
    enrollDate: string;
    payment: InitialPayment;
}

export default function StudentForm({ onSubmit, onCancel }: Props) {
    const { user } = useContext(AuthContext);
    const isViewer = user?.role === 'viewer';

    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const formRef = useRef<HTMLFormElement>(null);
    const [courses, setCourses] = useState<CourseOption[]>([]);
    const [error, setError] = useState<string>('');

    // — CROPPING STATE & REFS —
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [showCropper, setShowCropper] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [cropPreview, setCropPreview] = useState<string | null>(null);

    // — FORM STATE —
    const [form, setForm] = useState({
        schoolName: '',
        schoolCourse: '',
        name: '',
        email: '',
        phone: '',
        fathersName: '',
        grandfathersName: '',
        guardianNumber: '',
        address: '',
        municipality: '',
        wardNo: '',
        district: '',
        state: '',
        referredBy: '',
    });
    const [dob, setDob] = useState('');
    const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([
        {
            course: '',
            discount: '',
            enrollDate: new Date().toISOString().slice(0, 10),
            payment: {
                paymentDate: new Date().toISOString().slice(0, 10),
                receiptNumber: '',
                amount: 0,
            },
        },
    ]);
    const [discountErrors, setDiscountErrors] = useState<{ [idx: number]: string }>({});

    useEffect(() => {
        getCourses().then((cs) =>
            setCourses(
                cs.map((c) => ({
                    id: c.id,
                    name: c.name,
                    baseFee: c.baseFee,
                    maxViewerDiscount: c.maxViewerDiscount,
                }))
            )
        );
    }, []);

    // — VALIDATION HELPERS —
    function handleNumberKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
        if (!/[0-9]/.test(e.key)) e.preventDefault();
    }
    function validateEmail(email: string) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    function validatePhone(phone: string) {
        return /^\d{10}$/.test(phone);
    }

    // — FILE SELECT → OPEN MODAL —
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setImageFile(file);
        setCropPreview(null);
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImageUrl(reader.result as string);
                setShowCropper(true);
            };
            reader.readAsDataURL(file);
        } else {
            setImageUrl(null);
        }
    };

    const handleRemovePhoto = () => {
        setImageFile(null);
        setImageUrl(null);
        setCropPreview(null);
        setCroppedAreaPixels(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const onCropComplete = useCallback((_: Area, cropped: Area) => {
        setCroppedAreaPixels(cropped);
    }, []);

    const handleCropperDone = async () => {
        if (imageUrl && croppedAreaPixels) {
            const croppedImg = await getCroppedImg(imageUrl, croppedAreaPixels);
            setCropPreview(croppedImg);
        }
        setShowCropper(false);
        setImageUrl(null);
    };

    const handleCropperCancel = () => {
        setShowCropper(false);
        setImageUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // — ENROLLMENT HANDLERS —
    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'phone' || name === 'guardianNumber') {
            if (value.length > 10) return;
            if (!/^\d*$/.test(value)) return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEnrollmentChange = (
        idx: number,
        field: keyof Omit<EnrollmentRow, 'payment'>,
        value: string
    ) => {
        setEnrollments((prev) => {
            const copy = [...prev];
            // @ts-ignore
            copy[idx][field] = value;
            return copy;
        });
        if (field === 'discount') {
            const courseOpt = courses.find((c) => c.id === enrollments[idx].course);
            const discountVal = Number(value);
            const maxDiscount = isViewer ? courseOpt?.maxViewerDiscount : undefined;
            let err = '';
            if (discountVal < 0) err = 'Cannot be negative';
            else if (maxDiscount !== undefined && discountVal > maxDiscount) {
                err = `Max allowed is ${maxDiscount}`;
            }
            setDiscountErrors((prev) => ({ ...prev, [idx]: err }));
        }
    };

    const handlePaymentChange = (
        idx: number,
        field: keyof InitialPayment,
        value: string | number
    ) => {
        setEnrollments((prev) => {
            const copy = [...prev];
            // @ts-ignore
            copy[idx].payment[field] = field === 'amount' ? Number(value) : value;
            return copy;
        });
    };

    const addEnrollment = () =>
        setEnrollments((prev) => [
            ...prev,
            {
                course: '',
                discount: '',
                enrollDate: new Date().toISOString().slice(0, 10),
                payment: { paymentDate: new Date().toISOString().slice(0, 10), receiptNumber: '', amount: 0 },
            },
        ]);

    const removeEnrollment = (idx: number) =>
        setEnrollments((prev) => prev.filter((_, i) => i !== idx));

    // — NAVIGATION —
    const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setError('');
        if (step === 2 && Object.values(discountErrors).some(Boolean)) {
            setError('Fix discount errors.');
            return;
        }
        if (step === 1) {
            if (!form.name.trim()) return setError('Name is required to proceed.');
            if (!form.email.trim()) return setError('Email is required to proceed.');
            if (!validateEmail(form.email)) return setError('Please enter a valid email address.');
            if (!form.phone.trim()) return setError('Phone number is required to proceed.');
            if (!validatePhone(form.phone)) return setError('Phone must be 10 digits.');
            setStep(2);
        } else if (step === 2) {
            for (let i = 0; i < enrollments.length; i++) {
                if (!enrollments[i].course) {
                    setError(`Please select a course for enrollment #${i + 1}`);
                    return;
                }
            }
            setStep(3);
        } else if (step === 3) {
            setStep(4);
        }
    };

    const handleBack = () => {
        setError('');
        setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4) : 1));
    };

    // — SUBMIT —
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        if (Object.values(discountErrors).some(Boolean)) {
            setError('Fix discount errors.');
            setStep(2);
            return;
        }
        if (!form.name.trim()) {
            setError('Name is required to proceed.');
            setStep(1);
            return;
        }
        if (!form.email.trim()) {
            setError('Email is required to proceed.');
            setStep(1);
            return;
        }
        if (!validateEmail(form.email)) {
            setError('Please enter a valid email address.');
            setStep(1);
            return;
        }
        if (!form.phone.trim()) {
            setError('Phone number is required to proceed.');
            setStep(1);
            return;
        }
        if (!validatePhone(form.phone)) {
            setError('Phone must be 10 digits.');
            setStep(1);
            return;
        }
        for (let i = 0; i < enrollments.length; i++) {
            if (!enrollments[i].course) {
                setError(`Please select a course for enrollment #${i + 1}`);
                setStep(2);
                return;
            }
        }

        try {
            const payload = new FormData();
            payload.append('name', form.name);
            payload.append('email', form.email);
            payload.append('phone', form.phone);
            Object.entries(form).forEach(([k, v]) => {
                if (v && !['name', 'email', 'phone'].includes(k)) {
                    payload.append(k, String(v));
                }
            });
            if (dob) payload.append('dateOfBirth', dob);

            if (cropPreview) {
                const file = dataURLtoFile(cropPreview, imageFile?.name || 'photo.jpg');
                payload.append('image', file);
            } else if (imageFile) {
                payload.append('image', imageFile);
            }
            if (croppedAreaPixels) {
                payload.append('imageCrop', JSON.stringify(croppedAreaPixels));
            }

            const enrollmentPayload: EnrollmentInput[] = enrollments.map((en) => ({
                course: en.course,
                discount: Number(en.discount || 0),
                enrollDate: en.enrollDate,
                initialPayment: { ...en.payment },
            }));
            payload.append('enrollments', JSON.stringify(enrollmentPayload));

            await onSubmit(payload);
            onCancel();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        }
    };

    return (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-[2px]">
            <div className="relative flex max-h-[92vh] w-full max-w-[920px] flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.20)]">
                <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 px-6 py-5 max-sm:px-4">
                    {step > 1 ? (
                        <button
                            type="button"
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-emerald-200 bg-white p-0 text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                            onClick={handleBack}
                            aria-label="Back"
                        >
                            <BackArrowIcon />
                        </button>
                    ) : (
                        <div className="h-9 w-9 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1 text-center">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">Student management · Step {step} of 4</div>
                        <h1 className="m-0 text-[26px] font-bold tracking-[-0.03em] text-slate-950 max-sm:text-[22px]">Enroll Student</h1>
                        <p className="mb-0 mt-1 text-[13px] leading-5 text-slate-500">Enter student &amp; enrollment details</p>
                    </div>
                    <button
                        type="button"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-emerald-200 bg-white p-0 text-[16px] text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        onClick={onCancel}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                {error && <div className="mx-6 mt-4 rounded-[9px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700 max-sm:mx-4">{error}</div>}

                {/* Crop Modal */}
                {showCropper && imageUrl && (
                    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
                        <div className="w-full max-w-[440px] rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
                            <h3 className="mb-4 mt-0 text-[16px] font-bold text-slate-950">Crop your photo</h3>
                            <div className="relative aspect-square w-full overflow-hidden rounded-[12px] bg-slate-900">
                                <Cropper
                                    image={imageUrl}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                />
                            </div>
                            <div className="mt-4 flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                    onClick={handleCropperCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-600 bg-emerald-600 px-5 text-[13px] font-semibold text-white transition hover:border-emerald-700 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                    onClick={handleCropperDone}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <form ref={formRef} className="grid min-h-0 min-w-0 grid-cols-1 gap-y-4 gap-x-6 overflow-y-auto overflow-x-hidden px-6 py-5 md:grid-cols-2 max-sm:px-4" onSubmit={handleSubmit}>
                    {step === 1 && (
                        <>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="name">Name *</label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                    value={form.name}
                                    onChange={handleFieldChange}
                                    required
                                />
                            </div>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="email">Email *</label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                    value={form.email}
                                    onChange={handleFieldChange}
                                    required
                                />
                            </div>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="phone">Phone *</label>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                    value={form.phone}
                                    onChange={handleFieldChange}
                                    onKeyPress={handleNumberKeyPress}
                                    inputMode="numeric"
                                    required
                                />
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            {enrollments.map((en, idx) => {
                                const courseOpt = courses.find((c) => c.id === en.course);
                                const discountVal = Number(en.discount || 0);
                                const netFee = courseOpt ? Math.max(courseOpt.baseFee - discountVal, 0) : 0;
                                const available = courses.filter(
                                    (c) => !enrollments.some((e, i) => i !== idx && e.course === c.id)
                                );
                                return (
                                    <div key={idx} className="col-span-full min-w-0 rounded-[14px] border border-slate-200 bg-slate-50/60 p-4 md:p-5">
                                        <h3 className="mb-4 mt-0 text-[15px] font-bold text-slate-950">Enrollment #{idx + 1}</h3>
                                        <div className="min-w-0 px-1">
                                            <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Course *</label>
                                            <select
                                                className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                                value={en.course}
                                                onChange={(e) => handleEnrollmentChange(idx, 'course', e.target.value)}
                                                required
                                            >
                                                <option value="">Select…</option>
                                                {available.map((c) => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {courseOpt && (
                                            <div className="mt-3 grid grid-cols-1 gap-1.5 rounded-[10px] border border-slate-200 bg-white px-3.5 py-3 text-[12px] text-slate-600 sm:grid-cols-3 [&_p]:m-0">
                                                <p><strong>Base fee:</strong> {courseOpt.baseFee}</p>
                                                {isViewer && <p><strong>Max discount:</strong> {courseOpt.maxViewerDiscount}</p>}
                                                <p><strong>Net fee:</strong> {netFee}</p>
                                            </div>
                                        )}
                                        <div className="mt-4 grid min-w-0 grid-cols-1 gap-y-4 gap-x-5 md:grid-cols-2">
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Discount</label>
                                                <input
                                                    type="text"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                                    value={en.discount}
                                                    onChange={(e) => handleEnrollmentChange(idx, 'discount', e.target.value)}
                                                    onKeyPress={handleNumberKeyPress}
                                                    inputMode="numeric"
                                                />
                                                {discountErrors[idx] && <div className="mx-6 mt-4 rounded-[9px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700 max-sm:mx-4">{discountErrors[idx]}</div>}
                                            </div>
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Enroll Date</label>
                                                <input
                                                    type="date"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                                    value={en.enrollDate}
                                                    onChange={(e) => handleEnrollmentChange(idx, 'enrollDate', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-3 grid min-w-0 grid-cols-1 gap-y-4 gap-x-5 md:grid-cols-3">
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Payment Date</label>
                                                <input
                                                    type="date"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                                    value={en.payment.paymentDate}
                                                    onChange={(e) => handlePaymentChange(idx, 'paymentDate', e.target.value)}
                                                />
                                            </div>
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Receipt</label>
                                                <input
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                                    value={en.payment.receiptNumber}
                                                    onChange={(e) => handlePaymentChange(idx, 'receiptNumber', e.target.value)}
                                                />
                                            </div>
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Amount</label>
                                                <input
                                                    type="text"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                                    value={en.payment.amount === 0 ? '' : en.payment.amount}
                                                    onChange={(e) => handlePaymentChange(idx, 'amount', e.target.value)}
                                                    onKeyPress={handleNumberKeyPress}
                                                    inputMode="numeric"
                                                />
                                            </div>
                                        </div>
                                        {idx > 0 && (
                                            <button
                                                type="button"
                                                className="inline-flex h-10 items-center justify-center rounded-[9px] border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                                                onClick={() => removeEnrollment(idx)}
                                            >
                                                Remove this course
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                onClick={addEnrollment}
                               
                            >
                                + Add another course
                            </button>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">Academic Details (optional)</h3>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="schoolName">School/College Name</label>
                                <input
                                    id="schoolName"
                                    name="schoolName"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                    value={form.schoolName}
                                    onChange={handleFieldChange}
                                />
                            </div>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="schoolCourse">School/College Course</label>
                                <input
                                    id="schoolCourse"
                                    name="schoolCourse"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                    value={form.schoolCourse}
                                    onChange={handleFieldChange}
                                />
                            </div>

                            {/* Parent/Guardian (optional) */}
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">
                                Parent/Guardian (optional)
                            </h3>
                            {[
                                { name: 'fathersName', label: "Father's Name" },
                                { name: 'grandfathersName', label: "Grandfather's Name" },
                                { name: 'guardianNumber', label: 'Guardian Number', type: 'tel' },
                            ].map((f) => (
                                <div key={f.name} className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor={f.name}>
                                        {f.label}
                                    </label>
                                    <input
                                        id={f.name}
                                        name={f.name}
                                        type={f.type || 'text'}
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                        value={form[f.name as keyof typeof form]}
                                        onChange={handleFieldChange}
                                        onKeyPress={f.name === 'guardianNumber' ? handleNumberKeyPress : undefined}
                                        inputMode={f.name === 'guardianNumber' ? 'numeric' : undefined}
                                    />
                                </div>
                            ))}

                            {/* Other Details (optional) */}
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">
                                Other Details (optional)
                            </h3>
                            <div className="col-span-full grid min-w-0 grid-cols-1 gap-y-4 gap-x-6 md:grid-cols-2">
                                <div className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="dateOfBirth">
                                        Date of Birth
                                    </label>
                                    <input
                                        id="dateOfBirth"
                                        name="dateOfBirth"
                                        type="date"
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                        value={dob}
                                        onChange={(e) => setDob(e.target.value)}
                                    />
                                </div>
                                <div className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="referredBy">
                                        Referred By
                                    </label>
                                    <input
                                        id="referredBy"
                                        name="referredBy"
                                        type="text"
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                        value={form.referredBy}
                                        onChange={handleFieldChange}
                                    />
                                </div>
                            </div>

                            {/* Photo (optional) */}
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Photo</label>
                                {/* hidden file input */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                                {cropPreview ? (
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <img
                                            src={cropPreview}
                                            alt="Cropped Preview"
                                            className="h-[120px] w-[120px] rounded-[10px] border border-slate-200 object-cover"
                                        />
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                           
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Re-crop
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                                           
                                            onClick={handleRemovePhoto}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ) : imageFile ? (
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                            onClick={() => setShowCropper(true)}
                                        >
                                            Crop
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                                           
                                            onClick={handleRemovePhoto}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        Choose Image
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {step === 4 && (
                        <>
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">
                                Address Details (optional)
                            </h3>
                            {[
                                { name: 'address', label: 'Address' },
                                { name: 'municipality', label: 'Municipality' },
                                { name: 'wardNo', label: 'Ward No.', type: 'text' },
                                { name: 'district', label: 'District' },
                                { name: 'state', label: 'State' },
                            ].map((f) => (
                                <div key={f.name} className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor={f.name}>
                                        {f.label}
                                    </label>
                                    <input
                                        id={f.name}
                                        name={f.name}
                                        type={f.type || 'text'}
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                                        value={form[f.name as keyof typeof form]}
                                        onChange={handleFieldChange}
                                        onKeyPress={f.name === 'wardNo' ? handleNumberKeyPress : undefined}
                                        inputMode={f.name === 'wardNo' ? 'numeric' : undefined}
                                    />
                                </div>
                            ))}
                        </>
                    )}

                    {/* Navigation Buttons */}
                    <div className="col-span-full mt-1 flex items-center justify-end gap-2 border-t border-slate-100 pt-4 max-sm:flex-col-reverse max-sm:items-stretch">
                        {step === 1 ? (
                            <button type="button" className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200" onClick={handleNext}>
                                Next
                            </button>
                        ) : step === 2 || step === 3 ? (
                            <>
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                    onClick={handleNext}
                                >
                                    Next
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-600 bg-emerald-600 px-5 text-[13px] font-semibold text-white transition hover:border-emerald-700 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                   
                                >
                                    Enroll
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-200 bg-white px-4 text-[13px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button type="submit" className="inline-flex h-10 items-center justify-center rounded-[9px] border border-emerald-600 bg-emerald-600 px-5 text-[13px] font-semibold text-white transition hover:border-emerald-700 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200">
                                    Enroll
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

